(function () {
  'use strict';
  const id = value => /^[1-9]\d*$/.test(String(value));
  function readOffer(data) {
    const quantity = Number(data.quantity);
    const selections = data.options ? JSON.parse(data.options) : ['Fabric', 'Size', 'Design'].map((name, i) => ({name, value: [data.fabric, data.size, data.design][i]}));
    if (!Array.isArray(selections) || !selections.length || selections.length > 3 ||
        selections.some(s => typeof s.name !== 'string' || !s.name || typeof s.value !== 'string' || !s.value || s.value.toLowerCase() === 'custom choice') ||
        new Set(selections.map(s => s.name)).size !== selections.length) throw new Error('Invalid warehouse options');
    const options = selections.map(s => s.value), optionNames = selections.map(s => s.name);
    if (!id(data.stockId) || !id(data.catalogueId) || data.stockId === data.catalogueId ||
        !Number.isSafeInteger(quantity) || quantity < 1 || options.some(value => !value) ||
        data.design === 'Custom Choice') throw new Error('Invalid warehouse offer');
    return { stockId: String(data.stockId), catalogueId: String(data.catalogueId), quantity, options, optionNames };
  }
  function remainingStock(offer, cart) {
    if (!Array.isArray(cart?.items)) throw new Error('Unable to check your cart');
    const count = cart.items.filter(item => String(item.variant_id) === offer.stockId)
      .reduce((sum, item) => {
        if (!Number.isSafeInteger(item.quantity) || item.quantity < 0) throw new Error('Unable to check your cart');
        return sum + item.quantity;
      }, 0);
    return Math.max(0, offer.quantity - count);
  }
  function cartPayload(offer, quantity, available) {
    const count = Number(quantity);
    if (!Number.isSafeInteger(count) || count < 1 || count > available) throw new Error('Please choose an available quantity.');
    return { items: [{ id: offer.stockId, quantity: count, properties: {
      'Fulfillment': 'German warehouse', '_fbp_catalogue_variant': offer.catalogueId
    } }] };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { readOffer, remainingStock, cartPayload };
  if (typeof customElements === 'undefined' || customElements.get('fbp-eu-warehouse')) return;

  class Warehouse extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.scope = this.closest('[id^="ProductInfo-"]');
      this.form = this.closest('form');
      this.picker = this.scope?.querySelector('pr-option');
      this.quantity = this.form?.querySelector('[name="quantity"]');
      this.submit = this.form?.querySelector('[name="add"]');
      if (!this.scope || !this.form || !this.picker || !this.quantity || !this.submit) return;
      this.cards = Array.from(this.querySelectorAll('[data-stock-id]'));
      try {
        this.offers = this.cards.map(card => readOffer(card.dataset));
        if (!this.offers.length || new Set(this.offers.map(offer => offer.stockId)).size !== this.offers.length) return;
        for (const offer of this.offers) this.radios(offer);
      } catch { return; }
      this.originalMax = this.quantity.getAttribute('max');
      this.status = this.querySelector('[data-warehouse-status]');
      this.error = this.querySelector('[data-warehouse-error]');
      this.mto = this.querySelector('[data-warehouse-mto]');
      this.dialog = this.querySelector('dialog');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', event => this.click(event), { signal });
      this.scope.addEventListener('click', event => {
        if (!event.target.closest('pr-option label[for], pr-option input[type="radio"]')) return;
        if (this.pending) { event.preventDefault(); event.stopImmediatePropagation(); return; }
        this.choose(null);
      }, { capture: true, signal });
      this.picker.addEventListener('change', () => { if (!this.syncing) this.choose(null); }, { capture: true, signal });
      this.quantity.addEventListener('change', () => this.clampQuantity(), { signal });
      document.addEventListener('submit', event => {
        if (event.target !== this.form || (!this.selected && !this.pending)) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!this.pending && !this.uncertain) this.addToCart();
      }, { capture: true, signal });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog || event.target.closest('[data-image-close]')) this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('fbp-warehouse-image-open');
        if (this.imageTrigger?.isConnected) this.imageTrigger.focus({ preventScroll: true });
      }, { signal });
      this.selectionVersion = 0;
      this.choose(null, false);
      this.hidden = false;
      const requestedStock = new URL(location.href).searchParams.get('eu_stock');
      if (requestedStock) {
        const offer = this.offers.find(item => item.stockId === requestedStock);
        if (offer) {
          const version = this.selectionVersion;
          // Wait for the native picker to own price, image and variant updates.
          customElements.whenDefined('pr-option').then(() => {
            if (!signal.aborted && this.isConnected && this.selectionVersion === version) this.selectOffer(offer);
          });
        } else {
          this.showError('This warehouse option is no longer available. Please choose another warehouse option or made to order.');
        }
      }
    }
    disconnectedCallback() {
      if (this.controller) this.choose(null, false);
      this.controller?.abort(); this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('fbp-warehouse-image-open');
      this.scope?.classList.remove('fbp-warehouse-active');
      document.body.classList.remove('fbp-warehouse-product-active');
    }
    radios(offer) {
      return offer.optionNames.map((name, index) => {
        const input = Array.from(this.picker.querySelectorAll('input[type="radio"]'))
          .find(input => input.name === name && input.value === offer.options[index]);
        if (!input || input.disabled) throw new Error('This option is unavailable.');
        return input;
      });
    }
    click(event) {
      const expand = event.target.closest('[data-warehouse-expand]');
      if (expand) {
        const image = this.dialog.querySelector('img');
        image.src = expand.dataset.image; image.alt = expand.dataset.title;
        this.dialog.querySelector('[data-image-caption]').textContent = expand.dataset.title;
        this.imageTrigger = expand;
        this.dialog.showModal(); document.documentElement.classList.add('fbp-warehouse-image-open');
        return;
      }
      if (event.target.closest('[data-warehouse-mto]')) { if (!this.pending) this.choose(null); return; }
      const card = event.target.closest('[data-stock-id]');
      if (!card || this.pending) return;
      const offer = this.offers.find(item => item.stockId === card.dataset.stockId);
      if (this.selected === offer) { this.choose(null); return; }
      this.selectOffer(offer);
    }
    selectOffer(offer) {
      try {
        const radios = this.radios(offer);
        this.syncing = true;
        radios.forEach(input => { input.checked = true; });
        radios[radios.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
        this.choose(offer);
      } catch { this.showError('This warehouse option is unavailable. Please choose another option.'); }
      finally { this.syncing = false; }
    }
    choose(offer, updateUrl = true) {
      this.selectionVersion = (this.selectionVersion || 0) + 1;
      this.selected = offer;
      this.dataset.mode = offer ? 'EU' : 'MTO';
      this.scope.classList.toggle('fbp-warehouse-active', !!offer);
      document.body.classList.toggle('fbp-warehouse-product-active', !!offer);
      this.scope.querySelectorAll('.fbp-profile-favorite[data-fbp-product-favorite]').forEach(control => { control.hidden = !!offer; });
      document.dispatchEvent(new CustomEvent('fbp:warehouse-mode-changed', { detail: { mode: this.dataset.mode } }));
      this.cards.forEach(card => {
        const selected = card.dataset.stockId === offer?.stockId;
        card.classList.toggle('is-selected', selected);
        card.querySelector('[data-warehouse-select]').setAttribute('aria-pressed', String(selected));
      });
      this.mto.setAttribute('aria-pressed', String(!offer));
      this.picker.querySelectorAll('input[type="radio"]').forEach(input => {
        for (const label of input.labels || []) label.classList.toggle('fbp-warehouse-muted', !!offer && !input.checked);
      });
      if (offer) {
        this.quantity.max = String(offer.quantity);
        this.scope.querySelectorAll('.fbp-daki-addon input[type="checkbox"]').forEach(input => {
          if (input.checked) { input.checked = false; input.dispatchEvent(new Event('change', { bubbles: true })); }
        });
      } else if (this.originalMax === null) this.quantity.removeAttribute('max');
      else this.quantity.setAttribute('max', this.originalMax);
      this.clampQuantity();
      this.status.textContent = offer ? 'Warehouse stock selected: ' + offer.options.join(', ') + '. Shipping time 1–2 weeks.' : 'Made to order selected.';
      if (!this.uncertain) this.showError('');
      if (updateUrl) {
        const url = new URL(location.href);
        if (offer) {
          url.searchParams.set('variant', offer.catalogueId);
          url.searchParams.set('eu_stock', offer.stockId);
          url.hash = 'eu-warehouse';
        } else url.searchParams.delete('eu_stock');
        if (url.href !== location.href) history.replaceState(history.state, '', url);
      }
    }
    clampQuantity() {
      if (!this.selected) return;
      this.quantity.value = String(Math.max(1, Math.min(this.selected.quantity, Math.floor(Number(this.quantity.value) || 1))));
    }
    showError(message, review = false) {
      this.error.hidden = !message;
      this.error.querySelector('[data-error-text]').textContent = message;
      this.error.querySelector('a').hidden = !review;
    }
    async addToCart() {
      const offer = this.selected;
      this.pending = true;
      const locked = Array.from(this.scope.querySelectorAll('pr-option input, [name="quantity"], .qty-bt, [data-warehouse-mto], [data-warehouse-select]'))
        .map(control => ({ control, disabled: control.disabled }));
      locked.forEach(({ control }) => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.submit.setAttribute('aria-disabled', 'true');
      this.showError('');
      let mutationStarted = false;
      const requestSignal = () => AbortSignal.any([this.controller.signal, AbortSignal.timeout(15000)]);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const freshUrl = new URL(this.dataset.productUrl, location.origin);
        freshUrl.searchParams.set('section_id', this.dataset.sectionId);
        const [stockResponse, cartResponse] = await Promise.all([
          fetch(freshUrl, { cache: 'no-store', signal: requestSignal() }),
          fetch(root + 'cart.js', { cache: 'no-store', signal: requestSignal() })
        ]);
        if (!stockResponse.ok || !cartResponse.ok) throw new Error('Unable to check stock right now. Please try again.');
        const html = new DOMParser().parseFromString(await stockResponse.text(), 'text/html');
        const freshCard = Array.from(html.querySelectorAll('fbp-eu-warehouse [data-stock-id]'))
          .find(card => card.dataset.stockId === offer.stockId && card.dataset.catalogueId === offer.catalogueId);
        if (!freshCard) throw new Error('This warehouse option has sold out. Please choose another option.');
        const fresh = readOffer(freshCard.dataset);
        if (fresh.options.length !== offer.options.length || fresh.options.some((value, index) => value !== offer.options[index] || fresh.optionNames[index] !== offer.optionNames[index])) throw new Error('This warehouse option has changed. Please refresh the page.');
        const remaining = remainingStock(fresh, await cartResponse.json());
        const payload = cartPayload(fresh, this.quantity.value, remaining);
        if (!this.isConnected || this.selected !== offer) throw new Error('Your selection changed. Please choose the item again.');
        mutationStarted = true;
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload), signal: requestSignal()
        });
        if (!response.ok) throw new Error('Stock may have changed. Please review your cart before trying again.');
        const result = await response.json();
        if (!result.items?.some(item => String(item.variant_id || item.id) === offer.stockId)) throw new Error('Please review your cart to confirm this item.');
        this.uncertain = true;
        location.assign(root + 'cart');
      } catch (error) {
        this.uncertain = mutationStarted;
        this.showError(mutationStarted ? 'Please review your cart to confirm what was added before trying again.' : error.message, mutationStarted);
      } finally {
        locked.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.pending = false;
        this.removeAttribute('aria-busy');
        this.submit.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('fbp-eu-warehouse', Warehouse);
}());
